/**
 * The workspace-checklist archetype: one case, its items grouped by section, and a
 * completion meter.
 *
 * The status a bookkeeper reaches for most is דורש השלמה, because it is the only one the
 * client sees as an action. Setting it queues the request to the client in the same
 * transaction — so the office cannot ask for something without the client being told,
 * which is the whole point of the checklist being shared rather than internal.
 */
import { useState } from "react";
import { Link, useParams } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { formatDate } from "@onecpa/shared";
import {
  Badge,
  Button,
  CompletionMeter,
  EmptyState,
  ErrorState,
  Fact,
  PageHeader,
  Panel,
  Select,
  StatusBadge,
  TableSkeleton,
  Textarea,
} from "@onecpa/ui";
import { api, type CaseDetail as CaseDetailResponse } from "../api/client.js";
import { useSession } from "../app/session.js";

const ITEM_STATUSES: { value: string; label: string }[] = [
  { value: "pending", label: "ממתין" },
  { value: "received", label: "התקבל" },
  { value: "requires_completion", label: "דורש השלמה" },
  { value: "approved", label: "אושר" },
  { value: "not_applicable", label: "לא רלוונטי" },
];

export function CaseDetail({ kind }: { kind: "annual" | "wealth" }) {
  const { caseId } = useParams({
    from: kind === "annual" ? "/shell/annual/$caseId" : "/shell/wealth/$caseId",
  });
  const session = useSession();
  const queryClient = useQueryClient();

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["case", kind, session.orgId, caseId],
    queryFn: () => api.cases.get(session.orgId, kind, caseId),
  });

  const updateItem = useMutation({
    mutationFn: (input: { itemId: string; body: Record<string, unknown> }) =>
      api.cases.updateItem(session.orgId, kind, caseId, input.itemId, input.body),
    onSuccess: async (result) => {
      await queryClient.invalidateQueries({ queryKey: ["case"] });
      await queryClient.invalidateQueries({ queryKey: ["cases"] });
      if (result.progress.status === "ready") {
        session.toast("כל הפריטים נסגרו — התיק מוכן לעבודה ונוצרה משימה");
      }
    },
    onError: (mutationError: Error) => session.toast(mutationError.message, "danger"),
  });

  if (error) {
    return <ErrorState message={(error as Error).message} onRetry={() => void refetch()} />;
  }

  if (isLoading || !data) {
    return (
      <div className="p-5">
        <TableSkeleton rows={8} columns={3} />
      </div>
    );
  }

  const { workspace, items, files } = data;
  const sections = groupBySection(items);

  return (
    <>
      <PageHeader
        title={workspace.clientName}
        subtitle={
          kind === "annual"
            ? `דוח שנתי ${workspace.taxYear ?? ""}`
            : `הצהרת הון לתאריך ${workspace.declarationDate ? formatDate(workspace.declarationDate) : ""}`
        }
        action={<StatusBadge vocabulary="workspace" status={workspace.status} />}
      />

      <div className="grid gap-4 p-5 lg:grid-cols-3">
        <div className="flex flex-col gap-4 lg:col-span-2">
          {sections.map(([section, sectionItems]) => (
            <Panel key={section} title={section}>
              <ul className="divide-y divide-line-soft">
                {sectionItems.map((item) => (
                  <li key={item.id} className="px-3.5 py-2.5">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="min-w-0 flex-1 text-sm text-ink">
                        {item.label}
                        {!item.isRequired && (
                          <span className="ms-2 text-2xs text-ink-faint">(לא חובה)</span>
                        )}
                      </span>

                      {item.clientVisible ? (
                        <Badge tone="info">גלוי ללקוח</Badge>
                      ) : (
                        <Badge tone="neutral">פנימי</Badge>
                      )}

                      <Select
                        value={item.status}
                        aria-label={`מצב הפריט ${item.label}`}
                        className="w-36"
                        onChange={(event) =>
                          updateItem.mutate({
                            itemId: item.id,
                            body: { status: event.target.value },
                          })
                        }
                      >
                        {ITEM_STATUSES.map((status) => (
                          <option key={status.value} value={status.value}>
                            {status.label}
                          </option>
                        ))}
                      </Select>
                    </div>

                    {item.status === "requires_completion" && (
                      <OfficeNote
                        initial={item.officeNote ?? ""}
                        onSave={(note) =>
                          updateItem.mutate({ itemId: item.id, body: { officeNote: note } })
                        }
                      />
                    )}

                    {item.clientNote && (
                      <p className="mt-1.5 border-s-2 border-info-ink/40 ps-2 text-xs text-ink-muted">
                        הלקוח: {item.clientNote}
                      </p>
                    )}
                  </li>
                ))}
              </ul>
            </Panel>
          ))}

          {items.length === 0 && (
            <Panel>
              <EmptyState
                title="אין פריטים בתיק"
                description="ייתכן שתבניות הרשימה של המשרד ריקות. ניתן להגדיר אותן בהגדרות."
              />
            </Panel>
          )}
        </div>

        <div className="flex flex-col gap-4">
          <Panel title="מצב התיק">
            <dl className="flex flex-col gap-3 px-3.5 py-3">
              <Fact label="השלמה">
                <CompletionMeter
                  closed={
                    items.filter(
                      (item) => item.status === "approved" || item.status === "not_applicable",
                    ).length
                  }
                  total={items.length}
                  blocking={items.filter((item) => item.status === "requires_completion").length}
                />
              </Fact>
              <Fact label="מטפל">{workspace.assignedStaffName ?? "לא מוקצה"}</Fact>
              <Fact label="מועד">
                {workspace.dueDate ? (
                  <bdi className="tnum">{formatDate(workspace.dueDate)}</bdi>
                ) : (
                  "—"
                )}
              </Fact>
              <Fact label="לקוח">
                <Link
                  to="/clients/$clientId"
                  params={{ clientId: workspace.clientId }}
                  className="link-underline text-ledger"
                >
                  לכרטיס הלקוח
                </Link>
              </Fact>
            </dl>
          </Panel>

          <Panel title="מסמכים בתיק">
            {files.length > 0 ? (
              <ul className="divide-y divide-line-soft">
                {files.map((file) => (
                  <li key={file.id} className="flex items-center gap-2 px-3.5 py-2">
                    <span className="min-w-0 flex-1 truncate text-xs text-ink">
                      {file.displayName}
                    </span>
                    <StatusBadge vocabulary="scan" status={file.scanStatus} />
                  </li>
                ))}
              </ul>
            ) : (
              <EmptyState title="אין מסמכים" icon="file-text" />
            )}
          </Panel>
        </div>
      </div>
    </>
  );
}

/**
 * The note the client will read.
 *
 * Saved explicitly rather than on every keystroke: this text is sent to a client, and an
 * autosaved half-sentence is worse than no note at all.
 */
function OfficeNote({ initial, onSave }: { initial: string; onSave(note: string): void }) {
  const [value, setValue] = useState(initial);
  const dirty = value !== initial;

  return (
    <div className="mt-2 flex flex-col gap-1.5">
      <Textarea
        value={value}
        onChange={(event) => setValue(event.target.value)}
        placeholder="מה נדרש מהלקוח? ההערה תישלח אליו."
        aria-label="הערת המשרד ללקוח"
        className="min-h-14"
      />
      {dirty && (
        <span className="flex gap-2">
          <Button size="sm" variant="primary" onClick={() => onSave(value)}>
            שמירה ושליחה
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setValue(initial)}>
            ביטול
          </Button>
        </span>
      )}
    </div>
  );
}

function groupBySection(
  items: CaseDetailResponse["items"],
): [string, CaseDetailResponse["items"]][] {
  const map = new Map<string, CaseDetailResponse["items"]>();
  for (const item of items) {
    const list = map.get(item.section) ?? [];
    list.push(item);
    map.set(item.section, list);
  }
  return [...map.entries()];
}
